<template>
  <ValidationProvider
    tag="div"
    :vid="vid"
    :name="inputName"
    :rules="rules"
    class="input-field-container"
    :class="{ 'disable-focus': disableFocusStyle, inline: inline }"
    v-slot="{ errors }"
  >
    <div>
      <v-date-picker
        v-if="isDateInput()"
        :min-date="minDate"
        :max-date="maxDate"
        v-model="innerValue"
        locale="pt-BR"
        :mode="time ? 'dateTime' : 'date'"
        is24hr
        :first-day-of-week="1"
        :popover="{ visibility: isDisabled ? 'hide' : 'hover', placement: currentPlacement }"
        :masks="{
          input: 'DD/MM/YYYY'
        }"
      >
        <template v-slot="{ inputValue, inputEvents }">
          <div
            v-on="inputEvents"
            class="input-date-container"
            :class="{ error: errors[0], compact: compact }"
          >
            <p>
              {{ inputValue ? inputValue : placeholder }}
            </p>

            <i class="icon-calendar" />
          </div>
        </template>
      </v-date-picker>

      <v-date-picker
        v-else-if="isTimeInput()"
        v-model="innerValue"
        locale="pt-BR"
        mode="time"
        :is24hr="is24hr"
        :popover="{
          visibility: isDisabled ? 'hide' : 'hover',
          placement: currentPlacement
        }"
        :valid-hours="validHours"
        :masks="{
          input: 'HH:mm'
        }"
      >
        <template v-slot="{ inputValue, inputEvents }">
          <div
            v-on="inputEvents"
            class="input-date-container"
            :class="{ error: errors[0], compact: compact }"
          >
            <p>
              {{ inputValue ? inputValue : placeholder }}
            </p>

            <i class="icon-calendar" />
          </div>
        </template>
      </v-date-picker>

      <div v-if="!isDisabled" class="errors">
        <span>{{ errors[0] }}</span>
      </div>
    </div>
  </ValidationProvider>
</template>

<script>
  import { ValidationProvider } from 'vee-validate';

  export default {
    name: 'InputDateField',

    props: {
      type: {
        type: String,
        default: 'text',
        validator(value) {
          return ['date', 'time'].includes(value);
        }
      },

      vid: {
        type: String,
        default: undefined
      },

      placement: {
        type: String,
        default: 'bottom-start',
        validator(value) {
          return [
            'right',
            'bottom-start',
          ].includes(value);
        },
      },
      isDisabled: {
        type: Boolean,
        default: false
      },

      placeholder: {
        type: String,
        default: 'Placeholder'
      },

      rules: {
        type: [Object, String],
        default: ''
      },

      value: {
        type: [Date, String],
        default: ''
      },

      maxLength: {
        type: Number,
        required: false
      },

      disableFocusStyle: {
        type: Boolean,
        default: false
      },

      minDate: {
        type: [String, Date],
        required: false
      },

      maxDate: {
        type: [String, Date],
        required: false
      },

      time: {
        type: Boolean,
        default: false
      },

      inline: {
        type: Boolean,
        default: false
      },

      compact: {
        type: Boolean,
        default: false
      },

      validHours: {
        type: Object,
        default: () => ({
          min: 0,
          max: 23
        })
      },

      is24hr: {
        type: Boolean,
        default: true
      }
    },

    components: {
      ValidationProvider
    },

    data() {
      return {
        inputType: 'text',
        innerValue: '',
        inputName: '',
        currentPlacement: '',
      };
    },

    created() {
      if (this.value) {
        if (this.isTimeInput() && this.value) {
          if (typeof this.value === 'string' && this.value.includes(':')) {
            const today = new Date();
            const [hours, minutes] = this.value.split(':');
            today.setHours(parseInt(hours), parseInt(minutes), 0, 0);
            this.innerValue = today;
          } else {
            this.innerValue = this.value;
          }
        } else {
          this.innerValue = this.value;
        }
      }
    },

    mounted() {
      this.checkWidth();
      window.addEventListener('resize', () => this.checkWidth());
    },

    watch: {
      innerValue(value) {
        if (this.isTimeInput() && value) {
          let timeString;
          if (typeof value === 'string') {
            timeString = value;
          } else if (value instanceof Date) {
            const hours = value.getHours().toString().padStart(2, '0');
            const minutes = value.getMinutes().toString().padStart(2, '0');
            timeString = `${hours}:${minutes}`;
          }
          this.$emit('input', timeString);
        } else {
          this.$emit('input', value);
        }
      },

      value(value) {
        if (value !== this.innerValue) {
          if (this.isTimeInput() && value) {
            if (typeof value === 'string' && value.includes(':')) {
              const today = new Date();
              const [hours, minutes] = value.split(':');
              today.setHours(parseInt(hours), parseInt(minutes), 0, 0);
              this.innerValue = today;
            } else {
              this.innerValue = value;
            }
          } else {
            this.innerValue = value;
          }
        }
      }
    },

    methods: {
      isDateInput() {
        return this.type == 'date';
      },

      isTimeInput() {
        return this.type == 'time';
      },

      checkWidth() {
        const currentWidth = window.innerWidth;
        this.currentPlacement = currentWidth < 768 ? 'bottom-start' : this.placement;
      },
    },

    beforeDestroy() {
      window.removeEventListener('resize', () => this.checkWidth());
    }
  };
</script>

<style lang="scss">
  .v-select {
    max-height: 200px !important;
  }
  .input-field-container {
    margin-bottom: 1rem;
    width: 100%;

    &.inline {
      margin-bottom: 0;
    }

    &.disable-focus {
      .input {
        &:focus {
          outline: none;
          border-color: #dadada;
          box-shadow: none;
        }
      }
    }

    .input-date-container {
      border: 1px solid #dadada;
      border-radius: 6px;
      padding: 0.3rem 1rem;
      display: flex;
      align-items: center;
      color: #333;
      height: 48px;
      width: 100%;
      cursor: pointer;

      &.compact {
        height: 36px;
        padding: 0.2rem 0.75rem;

        p {
          font-size: 13px;
        }

        i {
          font-size: 1rem;
        }
      }

      &:focus {
        outline: none;
        border-color: $second-color;
        box-shadow: 0 0 0 1px $second-color, 0 0 0 3px $second-color-light-1;
      }

      &.error {
        border-color: $red-color;
        box-shadow: 0 0 0 1px $red-color, 0 0 0 3px $red-color-light;
      }

      i {
        margin-left: auto;
        font-size: 1.3rem;
        color: #979797;
      }
    }

    .vc-container {
      font-family: 'Poppins', sans-serif;
      border-color: transparent;
      background-color: transparent;
      z-index: 10000;
      background: $white-color;

      .vc-pane-container {
        .vc-header {
          .vc-title {
            color: #333;
            font-size: 14px;
            font-weight: 400;
            text-transform: capitalize;
          }
        }

        .vc-time-picker {
          justify-content: center;

          &.vc-bordered {
            border-top: 1px solid #DADADA;
          }

          svg {
            display: none;
          }

          .vc-date-time {
            margin-left: 0;

            .vc-date {
              display: none;
            }
          }
        }

        .vc-weeks {
          .vc-weekday {
            color: #333;
            font-weight: 600;
            padding: 1rem;
            font-size: 12px;
          }

          .vc-day {
            min-height: 40px;

            .vc-day-content  {
              background-color: unset;
            }

            .vc-highlights {
              .vc-day-layer {
                &.vc-day-box-center-center {
                  .vc-highlight {
                    background-color: $second-color !important;
                    border: none !important;

                    &.vc-highlight-base-middle {
                      background-color: $second-color-light-1 !important;
                    }
                  }
                }

                &.vc-day-box-right-center {
                  .vc-highlight-base-start {
                    background-color: $second-color-light-1 !important;
                  }

                  .vc-highlight-base-end {
                    background-color: $second-color-light-1 !important;
                  }
                }

                &.vc-day-box-left-center {
                  .vc-highlight-base-start {
                    background-color: $second-color-light-1 !important;
                  }

                  .vc-highlight-base-end {
                    background-color: $second-color-light-1 !important;
                  }
                }
              }
            }

            span {
              font-weight: 400 !important;
              font-size: 12px !important;
            }
          }
        }
      }
    }

    .errors {
      span {
        color: $red-color;
        font-size: 12px;
        margin-top: 0.5rem;
        font-weight: 400;
      }
    }
  }
</style>